import {
  AGENT_REFERRAL_COOKIE_KEY,
  AGENT_REFERRAL_COOKIE_MAX_AGE,
  SERVER_REFERRAL_COOKIE_KEY,
  isPersonalLinkPath,
  normalizeAgentReferralParam,
} from '~/utils/agentReferral';
import {
  AM_LINK_COOKIE_KEY,
  AM_LINK_COOKIE_MAX_AGE,
  AM_LINK_QUERY_KEY,
  normalizeAmLinkParam,
} from '~/utils/am/inviteLinks';

type QueryValue = string | string[] | null | undefined;

// Persist a partner referral code (?ref=CODE) from ANY page the visitor lands on
// or navigates to — not just /auth/register. The server middleware only sees
// full-page requests, so SPA navigations, client-only routes and SWR/CDN-cached
// pages could drop the code before signup. Both cookies are written so the
// register/Google/masterclass signups send it in the body AND the server
// fallback (dic_ref) agrees with it. An AM invite link (?am=CODE) is kept the
// same way in dic_am, which the signup endpoints read to assign that AM.
export default defineNuxtPlugin(() => {
  const router = useRouter();
  const cookieOptions = { maxAge: AGENT_REFERRAL_COOKIE_MAX_AGE, sameSite: 'lax' as const, path: '/' };
  const clientReferralCookie = useStatefulCookie<string | null>(AGENT_REFERRAL_COOKIE_KEY, cookieOptions);
  const serverReferralCookie = useCookie<string | null>(SERVER_REFERRAL_COOKIE_KEY, cookieOptions);
  const amLinkCookie = useCookie<string | null>(AM_LINK_COOKIE_KEY, {
    maxAge: AM_LINK_COOKIE_MAX_AGE,
    sameSite: 'lax',
    path: '/',
  });

  const captureReferral = (ref: QueryValue, path: string) => {
    if (isPersonalLinkPath(path)) return;
    const code = normalizeAgentReferralParam(ref);
    if (!code) return;
    clientReferralCookie.value = code;
    serverReferralCookie.value = code;
  };

  const captureAccountManagerLink = (am: QueryValue, path: string) => {
    if (isPersonalLinkPath(path)) return;
    const code = normalizeAmLinkParam(am);
    if (code) amLinkCookie.value = code;
  };

  const capture = (query: Record<string, unknown>, path: string) => {
    captureReferral(query.ref as QueryValue, path);
    captureAccountManagerLink(query[AM_LINK_QUERY_KEY] as QueryValue, path);
  };

  capture(router.currentRoute.value.query, router.currentRoute.value.path);
  router.afterEach((to) => {
    capture(to.query, to.path);
  });
});
